import { memo, useCallback, useMemo } from "react";

import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import type { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { PickersActionBarProps } from "@mui/x-date-pickers-pro";
import { useTranslation } from "react-i18next";

export type DatePickerActionBarProps = PickersActionBarProps & {
  stacked?: boolean;
};

const DatePickerActionBarComponent = ({
  onClear,
  onCancel,
  onSetToday,
  onAccept,
  actions,
  stacked = false,
  ...restProps
}: DatePickerActionBarProps) => {
  const { t } = useTranslation(["common"]);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const handleSetToday = useCallback(() => {
    onSetToday();
    onAccept();
  }, [onAccept, onSetToday]);

  const stackButtonsDirection = useMemo(() => {
    if (isMobile || !stacked || (actions?.length ?? [].length) < 4) {
      return "row";
    }
    return "column";
  }, [isMobile, stacked, actions]);

  return (
    <DialogActions {...restProps}>
      <Stack direction={stackButtonsDirection} spacing={isMobile ? 3 : 0.5}>
        <Stack direction="row" spacing={2}>
          {actions?.includes("cancel") && (
            <Button onClick={onCancel} variant="text">
              {t("common:cancel")}
            </Button>
          )}
          {actions?.includes("clear") && (
            <Button onClick={onClear} variant="text">
              {t("common:clear")}
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          {actions?.includes("today") && (
            <Button onClick={handleSetToday} variant="outlined">
              {t("common:now")}
            </Button>
          )}
          {actions?.includes("accept") && (
            <Button onClick={onAccept} variant="contained">
              {t("common:ok")}
            </Button>
          )}
        </Stack>
      </Stack>
    </DialogActions>
  );
};

export const DatePickerActionBar = memo(DatePickerActionBarComponent);

export default DatePickerActionBar;
